import { TextButton } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";

import { BugOutlineIcon } from "@/assets/icons/general/BugOutline";
import { FacebookIcon } from "@/assets/icons/general/Facebook";
import { InstagramIcon } from "@/assets/icons/general/Instagram";
import { LinkedinIcon } from "@/assets/icons/general/Linkedin";
import { YoutubeIcon } from "@/assets/icons/general/Youtube";
import { AndroidInstallModal } from "@/components/shared/AndroidInstallModal";
import { InstallAppBanner } from "@/components/shared/InstallAppBanner";
import { IOSInstallModal } from "@/components/shared/IOSInstallModal";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Link } from "@/components/ui/text/Link/Link";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { RouteConfig } from "@/config/route.config";
import { useDeviceType } from "@/hooks/useDeviceType";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { DemoContext } from "@/providers/demo/DemoContext";
import { RoutingUtils } from "@/util/routing.utils";

const Footer = () => {
  const { isDemoMode } = DemoContext.useDemo();
  const router = useRouter();
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [showAndroidModal, setShowAndroidModal] = useState(false);

  // PWA Install functionality
  const { isAndroid, isIOS } = useDeviceType();
  const {
    isInstallable,
    promptInstall,
    isDismissed,
    dismissPrompt,
    isInstalled,
    isIOS: isPWAIOS,
    isAndroid: isPWAAndroid,
    showManualInstructions,
  } = usePWAInstall();

  // Use PWA hook's device detection as it's more reliable
  const isIOSDevice = isPWAIOS || isIOS;
  const isAndroidDevice = isPWAAndroid || isAndroid;

  const handleInstall = () => {
    if (isIOSDevice) {
      // For iOS, show the instructions modal
      setShowIOSModal(true);
    } else if (isAndroidDevice) {
      // For Android, check if native prompt is available
      if (isInstallable) {
        // Trigger native install prompt
        promptInstall();
      } else {
        // Show manual instructions modal
        setShowAndroidModal(true);
      }
    }
  };

  const socialMediaLinks = [
    {
      icon: FacebookIcon,
      label: "Facebook",
      url: "https://www.facebook.com/wearelossdog",
    },
    {
      icon: InstagramIcon,
      label: "Instagram",
      url: "https://instagram.com/wearelossdog",
    },
    {
      icon: YoutubeIcon,
      label: "Youtube",
      url: "https://www.youtube.com/@lossdog",
    },
    {
      icon: LinkedinIcon,
      label: "Linkedin",
      url: "https://www.linkedin.com/company/lossdog/",
    },
  ];

  const isSettingsRoute = useMemo(() => {
    return RoutingUtils.isPathInRoutes(router.pathname, ["Settings"]);
  }, [router]);

  const isProfileEditRoute = useMemo(() => {
    return router.pathname.replace("/[section]", "") === RouteConfig.Edit.Index.slice(0, -1).replace("[section]", "");
  }, [router.pathname]);

  // Determine if we should show the install banner
  const shouldShowInstallBanner = useMemo(() => {
    // Only show on actual Android or iOS devices (not mobile responsive mode on desktop)
    if (!isAndroidDevice && !isIOSDevice) return false;

    // Don't show if already installed
    if (isInstalled || isDismissed) return false;

    // For Android, show if installable OR if manual instructions needed
    if (isAndroidDevice) return isInstallable || showManualInstructions;

    // For iOS, always show (can't reliably detect installability)
    if (isIOSDevice) return true;

    return false;
  }, [isInstalled, isDismissed, isAndroidDevice, isIOSDevice, isInstallable, showManualInstructions]);

  return (
    <footer
      className={cx(
        "no-print relative mt-auto flex flex-col items-center justify-center",
        isSettingsRoute || isProfileEditRoute ? "bg-elevation-fill-default-0" : "footer-gradient",
      )}
    >
      <div className="flex flex-col items-center justify-center gap-1-5 pt-3 pb-6">
        {/* PWA Install Banner */}
        {shouldShowInstallBanner && (
          <InstallAppBanner
            onInstall={handleInstall}
            onDismiss={dismissPrompt}
          />
        )}

        {/* iOS Install Modal */}
        <IOSInstallModal
          isOpen={showIOSModal}
          onClose={() => setShowIOSModal(false)}
        />

        {/* Android Install Modal */}
        <AndroidInstallModal
          isOpen={showAndroidModal}
          onClose={() => setShowAndroidModal(false)}
        />

        {/* Social Media Links */}
        <div className="flex w-full flex-row items-center justify-center gap-8 t:gap-14">
          {socialMediaLinks.map(
            (link) =>
              link.url && (
                <IconButton
                  key={link.url}
                  icon={link.icon}
                  onPress={() => window.open(link.url, "_blank", "noopener,noreferrer")}
                  label={link.label}
                  iconClassName={cx("h-7 w-7", link.label === "Youtube" && "h-8 w-[29px]")}
                  className="h-auto! w-auto! border-none bg-transparent dm:p-1! t:p-2! text-text-default-1"
                  variant="outlined"
                />
              ),
          )}
          {/* PWA Install Icon Button - Shows when dismissed but not installed */}
        </div>
        <Typography
          variant="prominent-1"
          size="labels-s"
          className="dm:text-labels-xs text-white"
        >
          © {new Date().getFullYear()} Lossdog, Inc. All rights reserved
        </Typography>
        <div className="flex flex-row items-center justify-center gap-2">
          <Link
            href={RouteConfig.External.PrivacyPolicy}
            target="_blank"
            className="text-success-200"
          >
            <Typography
              variant="prominent-1"
              size="body-paragraph-xs"
              className="text-success-200"
            >
              Privacy Policy
            </Typography>
          </Link>
          <Link
            href={RouteConfig.External.TermsOfService}
            target="_blank"
            className="text-success-200"
          >
            <Typography
              variant="prominent-1"
              size="body-paragraph-xs"
              className="text-success-200"
            >
              Terms of Service
            </Typography>
          </Link>

          <Link
            onClick={(e) => {
              if (isDemoMode) e.preventDefault();
            }}
            href={`${RouteConfig.BugReport}?source=${encodeURIComponent(window.location.href)}`}
            className="flex flex-row items-center gap-1 text-success-200"
          >
            <BugOutlineIcon className="h-4 w-4 text-success-200" />
            <Typography
              variant="prominent-1"
              size="body-paragraph-xs"
              className="text-success-200"
            >
              Report a bug
            </Typography>
          </Link>
          {(isAndroidDevice || isIOSDevice) && !isInstalled && isDismissed && (
            <TextButton
              className="text-success-200! underline"
              onPress={handleInstall}
            >
              Install App
            </TextButton>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
